import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SignaturePad from './SignaturePad';
import signatureService from '../services/signature';

const ContractSigner = ({ contract, onSigningComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savedSignature, setSavedSignature] = useState(null);
  const [landlordSignature, setLandlordSignature] = useState(null);
  const [tenantSignature, setTenantSignature] = useState(null);
  const [includeTenantSignature, setIncludeTenantSignature] = useState(false);
  const [useSavedForLandlord, setUseSavedForLandlord] = useState(true);

  // Fetch user's saved signature
  useEffect(() => {
    const fetchSignature = async () => {
      try {
        setLoading(true);
        const response = await signatureService.getUserSignature();
        if (response.exists) {
          setSavedSignature(response.signature.imageData);
          setLandlordSignature(response.signature.imageData);
        } else {
          setUseSavedForLandlord(false);
        }
      } catch (error) {
        console.error('Failed to fetch signature:', error);
        toast.error('Failed to load your saved signature');
        setUseSavedForLandlord(false);
      } finally {
        setLoading(false);
      }
    };

    fetchSignature();
  }, []);

  // Update landlord signature when switching between saved and new
  useEffect(() => {
    if (useSavedForLandlord && savedSignature) {
      setLandlordSignature(savedSignature);
    } else if (!useSavedForLandlord) {
      setLandlordSignature(null);
    }
  }, [useSavedForLandlord, savedSignature]);

  const handleSignContract = async () => {
    if (!landlordSignature) {
      toast.error('Landlord signature is required');
      return;
    }

    if (includeTenantSignature && !tenantSignature) {
      toast.error('Please provide tenant signature or uncheck the option');
      return;
    }

    try {
      setLoading(true);
      const signatures = {
        landlordSignature,
        tenantSignature: includeTenantSignature ? tenantSignature : '',
      };

      await signatureService.signContract(contract.id, signatures);
      toast.success('Contract signed successfully');

      if (onSigningComplete) {
        onSigningComplete();
      }
    } catch (error) {
      console.error('Failed to sign contract:', error);
      // The signature service already extracts error.response.data, so we access error.error directly
      const errorMessage = error?.error || error?.message || 'Failed to sign contract';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLandlordSignature = async (signatureData) => {
    setLandlordSignature(signatureData);

    // Optionally save this as the user's default signature
    if (!savedSignature && signatureData) {
      try {
        await signatureService.saveSignature(signatureData);
        setSavedSignature(signatureData);
        toast.success('Signature saved for future use');
      } catch (error) {
        console.error('Failed to save signature:', error);
        // Don't show error toast, as this is optional
      }
    }
  };

  // Check if contract is already fully signed
  const isFullySigned = contract.landlordSigned && contract.tenantSigned;
  const isPartiallySigned = contract.landlordSigned || contract.tenantSigned;

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="font-medium text-gray-900 mb-4">
          Sign Contract - {contract.propertyAddress}
        </h3>

        {loading ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Signature Status */}
            {isPartiallySigned && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Signature Status</h4>
                <div className="space-y-1 text-sm">
                  <p className="text-blue-700">
                    Landlord: {contract.landlordSigned ?
                      <span className="text-green-600 font-medium">✓ Signed</span> :
                      <span className="text-amber-600">Pending</span>
                    }
                  </p>
                  <p className="text-blue-700">
                    Tenant: {contract.tenantSigned ?
                      <span className="text-green-600 font-medium">✓ Signed</span> :
                      <span className="text-amber-600">Pending</span>
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Landlord Signature */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Landlord Signature (Bailleur)</h4>

              {savedSignature && (
                <div className="mb-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="landlordSignatureType"
                          checked={useSavedForLandlord}
                          onChange={() => setUseSavedForLandlord(true)}
                          className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Use saved signature
                        </span>
                      </label>
                      {useSavedForLandlord && savedSignature && (
                        <div className="mt-2 ml-6 border border-gray-300 rounded p-2 bg-white">
                          <img
                            src={savedSignature}
                            alt="Saved signature preview"
                            className="h-16 mx-auto"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <label className="flex items-center cursor-pointer mt-3">
                    <input
                      type="radio"
                      name="landlordSignatureType"
                      checked={!useSavedForLandlord}
                      onChange={() => setUseSavedForLandlord(false)}
                      className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Draw a new signature
                    </span>
                  </label>
                </div>
              )}

              {(!savedSignature || !useSavedForLandlord) && (
                <SignaturePad
                  initialSignature={useSavedForLandlord ? null : landlordSignature}
                  onSave={handleSaveLandlordSignature}
                  onClear={() => setLandlordSignature(null)}
                  label={savedSignature ? "" : "Draw your signature"}
                  height={150}
                />
              )}
            </div>

            {/* Option to include tenant signature */}
            <div className="border-t pt-4">
              <div className="flex items-center mb-4">
                <input
                  id="includeTenantSignature"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={includeTenantSignature}
                  onChange={(e) => setIncludeTenantSignature(e.target.checked)}
                />
                <label htmlFor="includeTenantSignature" className="ml-2 block text-sm text-gray-900">
                  Include tenant signature (if tenant has signed a physical copy)
                </label>
              </div>

              {includeTenantSignature && (
                <SignaturePad
                  initialSignature={tenantSignature}
                  onSave={setTenantSignature}
                  onClear={() => setTenantSignature(null)}
                  label="Tenant Signature (Locataire)"
                  height={150}
                />
              )}
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate(`/contracts/${contract.id}`)}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSignContract}
                className="btn-primary"
                disabled={loading || !landlordSignature}
              >
                {loading ? 'Signing...' : 'Sign Contract'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">About Signing Contracts</h3>
        <ul className="list-disc text-sm text-blue-700 ml-5 space-y-1">
          <li>Your electronic signature will be permanently added to the contract.</li>
          <li>The contract status will be updated to "signed" once both parties have signed.</li>
          <li>You can activate the contract after it has been fully signed.</li>
          <li>You can add the tenant's signature if they've signed a physical copy.</li>
          <li>The signed contract can be downloaded as a PDF for your records.</li>
        </ul>
      </div>
    </div>
  );
};

export default ContractSigner;
